import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Progress } from "@/components/ui/progress";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Trash, X, ArrowsClockwise } from "@phosphor-icons/react";
import { toast } from "sonner";
import useAccountScope from "@/hooks/useAccountScope";
import CleanupExtras from "@/components/cleanup/CleanupExtras";
import MessageDrilldown from "@/components/cleanup/MessageDrilldown";
import MessageList from "@/components/cleanup/MessageList";
import StatusBar from "@/components/cleanup/StatusBar";
import FilterBar, { EMPTY_BASE, hasBase } from "@/components/cleanup/FilterBar";

const PAGE = 100;
const ACTIVE = ["queued", "running"];
const fmt = (n) => Number(n || 0).toLocaleString();
const emptySel = () => ({ senders: [], years: [], months: [], categories: [], older_than_days: null });
const selCount = (s) =>
  s.senders.length + s.years.length + s.months.length + s.categories.length + (s.older_than_days ? 1 : 0);

function describe(sel) {
  const parts = [];
  if (sel.older_than_days) parts.push(`older than ${fmt(sel.older_than_days)} days`);
  if (sel.senders.length) parts.push(`${sel.senders.length} sender${sel.senders.length > 1 ? "s" : ""}`);
  if (sel.years.length || sel.months.length) parts.push([...sel.years, ...sel.months].join(", "));
  if (sel.categories.length) parts.push(sel.categories.join(", "));
  return parts.join(" + ");
}

function describeFilter(f) {
  if (!f) return "";
  return describe({
    senders: f.senders || [],
    years: f.years || [],
    months: f.months || [],
    categories: f.categories || [],
    older_than_days: f.older_than_days || null,
  });
}

function BucketRow({ label, sub, count, max, checked, onToggle, testid, indent = false }) {
  const pct = max ? Math.max(2, Math.round((count / max) * 100)) : 0;
  return (
    <label
      className={`relative flex items-center gap-3 px-3 py-1.5 border-b border-border cursor-pointer hover:bg-secondary/40 ${indent ? "pl-9" : ""}`}
      data-testid={testid}
    >
      <div className="absolute inset-y-0 left-0 bg-primary/10 pointer-events-none" style={{ width: `${pct}%` }} />
      <Checkbox checked={checked} onCheckedChange={onToggle} className="relative" />
      <span className="relative flex-1 min-w-0 truncate text-sm">
        {label}
        {sub ? <span className="ml-2 text-xs text-muted-foreground">{sub}</span> : null}
      </span>
      <span className="relative font-mono text-sm tabular-nums">{fmt(count)}</span>
    </label>
  );
}

function Panel({ title, children, right, className = "" }) {
  return (
    <section className={`border border-border bg-background min-w-0 ${className}`}>
      <header className="flex items-center justify-between px-3 py-2 border-b border-border">
        <h2 className="text-xs uppercase tracking-widest font-mono text-muted-foreground">{title}</h2>
        {right}
      </header>
      {children}
    </section>
  );
}

// Canonical form so a job's stored filter compares equal to a UI filter.
const filterSig = (f = {}) =>
  JSON.stringify(
    Object.keys(f || {})
      .sort()
      .map((k) => {
        const v = f[k];
        const empty = v == null || v === false || v === "" || (Array.isArray(v) && v.length === 0);
        return empty ? null : [k, Array.isArray(v) ? [...v].sort() : v];
      })
      .filter(Boolean)
  );

export default function Inbox() {
  const { accounts, accountParam, setSelectedAccountId } = useAccountScope();
  const [allMode, setAllMode] = useState(false);
  const activeId = allMode ? "all" : accountParam || accounts[0]?.account_id || null;

  const [summaries, setSummaries] = useState({});
  const [loadingId, setLoadingId] = useState(null);
  const [selections, setSelections] = useState({});
  const [jobsByAcct, setJobsByAcct] = useState({});
  const [expandedYears, setExpandedYears] = useState([]);
  const [senderQ, setSenderQ] = useState("");
  const [senderPaging, setSenderPaging] = useState(false);
  const [preview, setPreview] = useState({ count: null, loading: false });
  const [submitting, setSubmitting] = useState(false);
  const [drillOpen, setDrillOpen] = useState(false);
  const [extrasKey, setExtrasKey] = useState(0);
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState({});
  const [picked, setPicked] = useState(() => new Set());
  const [allMatching, setAllMatching] = useState(false);
  const [listVersion, setListVersion] = useState(0);
  const [confirmKind, setConfirmKind] = useState(null);
  const [archiveJob, setArchiveJob] = useState(null);
  const loadedIds = useRef([]);
  const filtersRef = useRef({});
  const prevActive = useRef({});
  const tick = useRef(0);

  filtersRef.current = filters;
  const base = (activeId && filters[activeId]) || EMPTY_BASE;
  const baseKey = JSON.stringify(base);
  const archivedView = view === "archived";

  // The Summary view needs a single account's aggregates.
  useEffect(() => {
    if (allMode && view === "summary") setView("list");
  }, [allMode, view]);
  const summary = activeId ? summaries[activeId] : null;
  const sel = useMemo(() => (activeId && selections[activeId]) || emptySel(), [activeId, selections]);
  const jobs = (activeId && jobsByAcct[activeId]) || [];
  const hasActiveJob = jobs.some((j) => ACTIVE.includes(j.status));
  const indexStatus = summary?.account?.index_status;

  const loadSummary = useCallback(async (id, opts = {}) => {
    if (!id || id === "all") return;
    if (!opts.silent) setLoadingId(id);
    try {
      const b = filtersRef.current[id] || EMPTY_BASE;
      const { data } = await api.get(`/cleanup/accounts/${id}/summary`, {
        params: {
          top: PAGE,
          sender_q: opts.q || undefined,
          q: b.q.trim() || undefined,
          category: b.category || undefined,
          received_after: b.after || undefined,
          received_before: b.before || undefined,
        },
      });
      setSummaries((s) => ({ ...s, [id]: data }));
    } catch (e) {
      if (!opts.silent) toast.error(e?.response?.data?.detail || "Failed to load account summary");
    } finally {
      if (!opts.silent) setLoadingId((cur) => (cur === id ? null : cur));
    }
  }, []);

  // Tab data loads only when the tab is first opened.
  useEffect(() => {
    if (activeId && !summaries[activeId]) loadSummary(activeId, { q: senderQ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId]);

  const loadJobs = useCallback(
    async (id) => {
      if (!id) return;
      try {
        const { data } = await api.get("/cleanup/jobs", {
          params: { account_id: id === "all" ? undefined : id, limit: 15 },
        });
        const items = data.items || [];
        setJobsByAcct((j) => ({ ...j, [id]: items }));
        const nowActive = new Set(items.filter((j) => ACTIVE.includes(j.status)).map((j) => j.job_id));
        const was = prevActive.current[id] || new Set();
        const finished = [...was].some((jid) => !nowActive.has(jid));
        prevActive.current[id] = nowActive;
        tick.current += 1;
        if (finished || (nowActive.size && tick.current % 3 === 0)) {
          loadSummary(id, { silent: true, q: senderQ });
          if (finished) setListVersion((v) => v + 1);
        }
      } catch (_) {
        /* keep polling */
      }
    },
    [loadSummary, senderQ]
  );

  useEffect(() => {
    if (!activeId) return undefined;
    loadJobs(activeId);
    const t = setInterval(() => loadJobs(activeId), hasActiveJob ? 2000 : 10000);
    return () => clearInterval(t);
  }, [activeId, hasActiveJob, loadJobs]);

  // Keep counts current while background sync is still indexing.
  useEffect(() => {
    if (!activeId || !["indexing", "pending"].includes(indexStatus)) return undefined;
    const t = setInterval(() => loadSummary(activeId, { silent: true, q: senderQ }), 8000);
    return () => clearInterval(t);
  }, [activeId, indexStatus, loadSummary, senderQ]);

  // Debounced server-side sender search.
  const firstSearch = useRef(true);
  useEffect(() => {
    if (firstSearch.current) {
      firstSearch.current = false;
      return undefined;
    }
    if (!activeId) return undefined;
    const t = setTimeout(() => loadSummary(activeId, { silent: true, q: senderQ }), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [senderQ]);

  // Re-query summary when chips/search/date change; reset hand-picked rows.
  const firstBase = useRef(true);
  useEffect(() => {
    setPicked(new Set());
    setAllMatching(false);
    if (firstBase.current) {
      firstBase.current = false;
      return;
    }
    if (activeId) loadSummary(activeId, { silent: true, q: senderQ });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, baseKey, view]);

  const apiBase = useMemo(
    () => ({
      categories: base.category ? [base.category] : [],
      q: base.q.trim() || undefined,
      received_after: base.after || undefined,
      received_before: base.before || undefined,
    }),
    [base]
  );
  const listFilter = useMemo(() => ({ ...(archivedView ? {} : apiBase), archived: archivedView }), [apiBase, archivedView]);
  const summaryFilter = useMemo(
    () => ({
      ...apiBase,
      senders: sel.senders,
      years: sel.years,
      months: sel.months,
      categories: sel.categories.length ? sel.categories : apiBase.categories,
      older_than_days: sel.older_than_days,
    }),
    [apiBase, sel]
  );
  // Filter whose match count drives the header and the confirmation text (null = nothing to count).
  const countFilter = useMemo(() => {
    if (view === "summary") return selCount(sel) > 0 ? summaryFilter : null;
    return hasBase(base) || archivedView ? listFilter : null;
  }, [view, sel, summaryFilter, base, archivedView, listFilter]);
  const countKey = JSON.stringify(countFilter);

  // Exact match count, computed server-side.
  useEffect(() => {
    if (!activeId || !countFilter) {
      setPreview({ count: null, loading: false });
      return undefined;
    }
    setPreview((p) => ({ ...p, loading: true }));
    let cancelled = false;
    const t = setTimeout(async () => {
      try {
        const { data } = await api.post("/cleanup/preview", { account_id: activeId, filter: countFilter });
        if (!cancelled) setPreview({ count: data.count, loading: false });
      } catch (_) {
        if (!cancelled) setPreview({ count: null, loading: false });
      }
    }, 300);
    return () => {
      cancelled = true;
      clearTimeout(t);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeId, countKey, listVersion]);

  const toggle = (key, value) => {
    setSelections((all) => {
      const cur = all[activeId] || emptySel();
      const has = cur[key].includes(value);
      return {
        ...all,
        [activeId]: { ...cur, [key]: has ? cur[key].filter((v) => v !== value) : [...cur[key], value] },
      };
    });
  };
  const clearSel = () => setSelections((all) => ({ ...all, [activeId]: emptySel() }));

  const loadMoreSenders = async () => {
    if (!summary) return;
    setSenderPaging(true);
    try {
      const { data } = await api.get(`/cleanup/accounts/${activeId}/summary`, {
        params: {
          top: PAGE,
          sender_offset: summary.senders.items.length,
          sender_q: senderQ || undefined,
          senders_only: true,
        },
      });
      setSummaries((s) => ({
        ...s,
        [activeId]: {
          ...s[activeId],
          senders: { ...data.senders, items: [...s[activeId].senders.items, ...data.senders.items] },
        },
      }));
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to load more senders");
    } finally {
      setSenderPaging(false);
    }
  };

  // What the current selection acts on: a filter (server resolves everything matching) or hand-picked ids.
  const target = useMemo(() => {
    if (view === "summary") {
      return selCount(sel) > 0 ? { filters: [summaryFilter], count: preview.count, label: describe(sel) } : null;
    }
    if (allMatching) return { filters: [listFilter], count: preview.count, label: `all matching` };
    if (picked.size) {
      const ids = [...picked];
      const filters = [];
      for (let i = 0; i < ids.length; i += 1000) filters.push({ email_ids: ids.slice(i, i + 1000) });
      return { filters, count: picked.size, label: `${picked.size} selected` };
    }
    return null;
  }, [view, sel, summaryFilter, allMatching, listFilter, picked, preview.count]);

  // Deleting is blocked only when an active job already covers this exact filter.
  const targetDeleting = useMemo(() => {
    if (!target) return false;
    const active = jobs.filter((j) => ACTIVE.includes(j.status)).map((j) => filterSig(j.filter));
    return target.filters.every((f) => active.includes(filterSig(f)));
  }, [target, jobs]);

  const afterAction = () => {
    setPicked(new Set());
    setAllMatching(false);
    clearSel();
    setListVersion((v) => v + 1);
    loadJobs(activeId);
    loadSummary(activeId, { silent: true, q: senderQ });
    setExtrasKey((k) => k + 1);
  };

  // Large archives run server-side; poll until done so the status bar can show progress.
  const watchArchive = (jobId, total) => {
    setArchiveJob({ archived: 0, total });
    const t = setInterval(async () => {
      try {
        const { data } = await api.get(`/cleanup/archive-jobs/${jobId}`);
        setArchiveJob({ archived: data.archived, total: data.total });
        if (data.status !== "running") {
          clearInterval(t);
          setArchiveJob(null);
          if (data.status === "completed") toast.success(`Archived ${fmt(data.archived)} messages`);
          else toast.error(data.error || "Archive failed");
          afterAction();
        }
      } catch (_) {
        clearInterval(t);
        setArchiveJob(null);
      }
    }, 1500);
  };

  // Saved filters, rules and suggestions describe a full filter; load it into the Summary view.
  const applyFilter = (f) => {
    setSelections((all) => ({
      ...all,
      [activeId]: {
        senders: f.senders || [],
        years: f.years || [],
        months: f.months || [],
        categories: f.categories || [],
        older_than_days: f.older_than_days || null,
      },
    }));
    setFilters((x) => ({
      ...x,
      [activeId]: { category: null, q: f.q || "", after: f.received_after || "", before: f.received_before || "" },
    }));
    setView("summary");
  };

  const runAction = async (kind) => {
    if (!target) return;
    if (kind === "delete" && targetDeleting) {
      toast.error("A deletion is already running for this filter");
      return;
    }
    setSubmitting(true);
    try {
      const kinds = { archive: "Archived", restore: "Restored" };
      let done = 0;
      for (const f of target.filters) {
        const { data } = await api.post(kind === "delete" ? "/cleanup/jobs" : `/cleanup/${kind}`, {
          account_id: activeId,
          filter: f,
        });
        done += data.archived || data.restored || 0;
        if (data.queued) {
          watchArchive(data.job_id, data.total);
          done = null;
        }
      }
      if (kind === "delete") toast.success("Deletion job started");
      else if (done !== null) toast.success(`${kinds[kind]} ${fmt(done)} message${done === 1 ? "" : "s"}`);
      setConfirmKind(null);
      afterAction();
    } catch (e) {
      toast.error(e?.response?.data?.detail || `Could not ${kind}`);
    } finally {
      setSubmitting(false);
    }
  };

  const togglePick = (id) =>
    setPicked((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  const pickLoaded = () => setPicked(new Set(loadedIds.current));
  const setBase = (next) => setFilters((f) => ({ ...f, [activeId]: next }));

  const cancelJob = async (jobId) => {
    try {
      await api.post(`/cleanup/jobs/${jobId}/cancel`);
      loadJobs(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Cancel failed");
    }
  };

  const jobAction = async (jobId, action) => {
    try {
      await api.post(`/cleanup/jobs/${jobId}/${action}`);
      loadJobs(activeId);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Action failed");
    }
  };

  const retryIndexing = async () => {
    try {
      await api.post(`/accounts/${activeId}/backfill`);
      toast.success("Sync restarted");
      loadSummary(activeId, { silent: true, q: senderQ });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Retry failed");
    }
  };

  const yearsData = summary?.years || [];
  const monthsByYear = useMemo(() => {
    const m = {};
    (summary?.months || []).forEach((x) => {
      const y = x.bucket.slice(0, 4);
      (m[y] = m[y] || []).push(x);
    });
    return m;
  }, [summary]);
  const maxOf = (arr) => arr.reduce((a, b) => Math.max(a, b.count), 0);

  if (!accounts.length) {
    return (
      <div className="p-6" data-testid="inbox-page">
        <div className="section-tag mb-2">/ Inbox</div>
        <h1 className="font-display text-4xl mb-4">Bulk cleanup</h1>
        <p className="text-sm text-muted-foreground">Connect an account to start clearing your inbox.</p>
      </div>
    );
  }

  const senders = summary?.senders?.items || [];
  const selected = selCount(sel) > 0;

  return (
    <div className="p-4 sm:p-6 pb-40" data-testid="inbox-page">
      <div className="section-tag mb-2">/ Inbox</div>
      <div className="flex items-end justify-between gap-3 mb-4">
        <h1 className="font-display text-4xl">Bulk cleanup</h1>
        <Button
          variant="outline"
          size="sm"
          onClick={() => {
            loadSummary(activeId, { q: senderQ });
            setListVersion((v) => v + 1);
          }}
          data-testid="refresh-summary"
        >
          <ArrowsClockwise size={14} className="mr-1.5" /> Refresh
        </Button>
      </div>

      <div className="overflow-x-auto mb-4">
        <div className="inline-flex border border-border bg-background" role="tablist">
          {accounts.length > 1 && (
            <button
              type="button"
              role="tab"
              aria-selected={allMode}
              onClick={() => setAllMode(true)}
              className={`px-3 py-2 text-xs sm:text-sm font-mono border-r border-border whitespace-nowrap ${
                allMode ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-secondary/40"
              }`}
              data-testid="account-tab-all"
            >
              All inboxes
            </button>
          )}
          {accounts.map((a) => {
            const active = !allMode && a.account_id === activeId;
            return (
              <button
                key={a.account_id}
                type="button"
                role="tab"
                aria-selected={active}
                onClick={() => {
                  setAllMode(false);
                  setSelectedAccountId(a.account_id);
                }}
                className={`px-3 py-2 text-xs sm:text-sm font-mono border-r border-border whitespace-nowrap flex items-center gap-1.5 ${
                  active ? "bg-foreground text-background" : "text-muted-foreground hover:text-foreground hover:bg-secondary/40"
                }`}
                data-testid={`account-tab-${a.account_id}`}
              >
                {a.email || `${a.provider} (${a.account_id})`}
                {a.index_status === "indexing" && <span title="Syncing in the background">⏳</span>}
                {a.index_status === "error" && <span title="Sync stalled">⚠️</span>}
              </button>
            );
          })}
        </div>
      </div>

      {summary && (
        <div className="mb-4 text-xs font-mono" data-testid="sync-status">
          {(() => {
            const acct = summary.account;
            const indexed = acct.indexed ?? summary.total;
            const est = acct.mailbox_total && acct.mailbox_total >= indexed ? acct.mailbox_total : null;
            const pct = est ? Math.min(100, Math.round((indexed / est) * 100)) : 0;
            const working = ["indexing", "pending"].includes(indexStatus);
            return (
              <>
                <div className="flex flex-wrap items-center gap-x-6 gap-y-1">
                  <span>
                    {working ? "Indexing… " : "Indexed "}
                    <b data-testid="total-count">{fmt(indexed)}</b>
                    {est ? ` of ~${fmt(est)}` : ""} messages
                  </span>
                  {indexStatus === "complete" && <span className="text-muted-foreground">Sync: up to date</span>}
                  {indexStatus === "error" && (
                    <span className="text-destructive flex items-center gap-2" data-testid="sync-error">
                      {acct.backfill_error || "provider error"}
                    </span>
                  )}
                  {(indexStatus === "error" || (working && !acct.backfill_running)) && (
                    <button type="button" className="underline" onClick={retryIndexing} data-testid="retry-sync">
                      Resume indexing
                    </button>
                  )}
                </div>
                {working && est ? <Progress value={pct} className="h-1.5 mt-1.5 max-w-md" data-testid="index-progress" /> : null}
              </>
            );
          })()}
        </div>
      )}

      <div className="flex flex-wrap items-center justify-between gap-3 mb-3">
        <div className="inline-flex border border-border bg-background" role="tablist" data-testid="view-toggle">
          {[
            ["list", "List"],
            ...(allMode ? [] : [["summary", "Summary"]]),
            ["archived", `Archived${summary?.archived_count ? ` (${fmt(summary.archived_count)})` : ""}`],
          ].map(([key, label]) => (
            <button
              key={key}
              type="button"
              role="tab"
              aria-selected={view === key}
              onClick={() => setView(key)}
              className={`px-3 py-1.5 text-xs font-mono border-r border-border last:border-r-0 ${
                view === key ? "bg-foreground text-background" : "text-muted-foreground hover:bg-secondary/40"
              }`}
              data-testid={`view-${key}`}
            >
              {label}
            </button>
          ))}
        </div>
        {view !== "summary" && (
          <span className="text-xs font-mono text-muted-foreground" data-testid="list-count">
            {countFilter
              ? `${fmt(preview.count)} matching`
              : `${fmt(allMode ? accounts.reduce((a, x) => a + (x.indexed || 0), 0) : summary?.total)} messages`}
          </span>
        )}
      </div>

      {view !== "archived" && <FilterBar base={base} onChange={setBase} />}

      {view !== "summary" && (
        <section className="border border-border bg-background mb-4" data-testid="list-panel">
          <header className="flex flex-wrap items-center gap-3 px-3 py-2 border-b border-border text-xs font-mono">
            <label className="flex items-center gap-2 cursor-pointer">
              <Checkbox
                checked={allMatching || (picked.size > 0 && picked.size === loadedIds.current.length)}
                disabled={allMatching}
                onCheckedChange={(v) => (v ? pickLoaded() : setPicked(new Set()))}
                data-testid="select-loaded"
              />
              Select loaded
            </label>
            {allMatching ? (
              <span data-testid="all-matching-on">
                All <b>{fmt(preview.count)}</b> matching selected{" "}
                <button type="button" className="underline" onClick={() => setAllMatching(false)}>Undo</button>
              </span>
            ) : (
              countFilter && preview.count > 0 && (
                <button type="button" className="underline" onClick={() => setAllMatching(true)} data-testid="select-all-matching">
                  Select all {fmt(preview.count)} matching this filter to delete all
                </button>
              )
            )}
          </header>
          <MessageList
            accountId={activeId}
            filter={listFilter}
            version={listVersion}
            accountLabels={allMode ? Object.fromEntries(accounts.map((a) => [a.account_id, a.email])) : null}
            picked={picked}
            allMatching={allMatching}
            onTogglePick={togglePick}
            onPickLoaded={(ids) => {
              loadedIds.current = ids;
            }}
          />
        </section>
      )}

      {!summary && loadingId === activeId && (
        <p className="text-sm text-muted-foreground font-mono" data-testid="summary-loading">Loading summary…</p>
      )}

      {summary && view === "summary" && (
        <div className="grid gap-4 lg:grid-cols-3 mb-4">
          <Panel
            title="Top senders"
            right={
              <input
                value={senderQ}
                onChange={(e) => setSenderQ(e.target.value)}
                placeholder="Search senders"
                className="h-7 w-40 border border-border bg-background px-2 text-xs font-mono"
                data-testid="sender-search"
              />
            }
          >
            <div className="max-h-[60vh] overflow-y-auto" data-testid="sender-list">
              {senders.length === 0 && <p className="p-3 text-sm text-muted-foreground">No senders yet.</p>}
              {senders.map((s) => (
                <BucketRow
                  key={s.sender}
                  label={s.name && s.name !== s.sender ? s.name : s.sender}
                  sub={s.name && s.name !== s.sender ? s.sender : null}
                  count={s.count}
                  max={senders[0]?.count}
                  checked={sel.senders.includes(s.sender)}
                  onToggle={() => toggle("senders", s.sender)}
                  testid={`sender-row-${s.sender}`}
                />
              ))}
              {summary.senders.has_more && (
                <button
                  type="button"
                  onClick={loadMoreSenders}
                  disabled={senderPaging}
                  className="w-full py-2 text-xs font-mono uppercase tracking-widest hover:bg-secondary/40"
                  data-testid="load-more-senders"
                >
                  {senderPaging ? "Loading…" : "Load more senders"}
                </button>
              )}
            </div>
          </Panel>

          <Panel title="By date">
            <div className="max-h-[60vh] overflow-y-auto" data-testid="date-list">
              {yearsData.length === 0 && <p className="p-3 text-sm text-muted-foreground">Nothing indexed yet.</p>}
              {yearsData.map((y) => {
                const open = expandedYears.includes(y.bucket);
                return (
                  <div key={y.bucket}>
                    <div className="flex items-stretch">
                      <button
                        type="button"
                        className="w-7 text-xs text-muted-foreground hover:text-foreground border-b border-border"
                        onClick={() => setExpandedYears((e) => (open ? e.filter((v) => v !== y.bucket) : [...e, y.bucket]))}
                        aria-label={open ? "Collapse year" : "Expand year"}
                      >
                        {open ? "▾" : "▸"}
                      </button>
                      <div className="flex-1 min-w-0">
                        <BucketRow
                          label={y.bucket}
                          count={y.count}
                          max={maxOf(yearsData)}
                          checked={sel.years.includes(y.bucket)}
                          onToggle={() => toggle("years", y.bucket)}
                          testid={`year-row-${y.bucket}`}
                        />
                      </div>
                    </div>
                    {open &&
                      (monthsByYear[y.bucket] || []).map((m) => (
                        <BucketRow
                          key={m.bucket}
                          label={m.bucket}
                          count={m.count}
                          max={maxOf(monthsByYear[y.bucket])}
                          checked={sel.months.includes(m.bucket)}
                          onToggle={() => toggle("months", m.bucket)}
                          testid={`month-row-${m.bucket}`}
                          indent
                        />
                      ))}
                  </div>
                );
              })}
            </div>
          </Panel>

          <Panel title="By category">
            <div className="max-h-[60vh] overflow-y-auto" data-testid="category-list">
              {summary.categories.map((c) => (
                <BucketRow
                  key={c.bucket}
                  label={c.bucket}
                  count={c.count}
                  max={maxOf(summary.categories)}
                  checked={sel.categories.includes(c.bucket)}
                  onToggle={() => toggle("categories", c.bucket)}
                  testid={`category-row-${c.bucket}`}
                />
              ))}
            </div>
          </Panel>
        </div>
      )}

      {!allMode && (
        <details className="mb-4" data-testid="tools-section">
          <summary className="cursor-pointer text-xs uppercase tracking-widest font-mono text-muted-foreground mb-2">
            Suggestions, saved filters &amp; auto-clean
          </summary>
          <CleanupExtras
            accountId={activeId}
            sel={view === "summary" ? summaryFilter : apiBase}
            hasSelection={view === "summary" ? selected || hasBase(base) : hasBase(base)}
            onApply={applyFilter}
            refreshKey={extrasKey}
          />
        </details>
      )}

      <Panel
        title={`Deletion jobs${hasActiveJob ? " · running · progress updates every 2s" : ""}`}
        right={<span className="text-xs font-mono text-muted-foreground">{jobs.length} recent</span>}
      >
        <div data-testid="jobs-panel">
          {jobs.length === 0 && <p className="p-3 text-sm text-muted-foreground">No deletion jobs yet.</p>}
          {jobs.map((j) => {
            const done = j.deleted + j.failed;
            const pct = j.total ? Math.min(100, Math.round((done / j.total) * 100)) : 0;
            const active = ACTIVE.includes(j.status);
            const paused = j.status === "paused";
            return (
              <div key={j.job_id} className="px-3 py-2 border-b border-border" data-testid={`job-${j.job_id}`}>
                <div className="flex items-center justify-between gap-3 text-xs font-mono">
                  <span className="truncate">{describeFilter(j.filter)}</span>
                  <span className="flex items-center gap-3 shrink-0">
                    <span className={j.status === "failed" ? "text-destructive" : "text-muted-foreground"}>{j.status}</span>
                    {active && (
                      <button type="button" onClick={() => jobAction(j.job_id, "pause")} className="underline" data-testid={`pause-job-${j.job_id}`}>
                        Pause
                      </button>
                    )}
                    {paused && (
                      <button type="button" onClick={() => jobAction(j.job_id, "resume")} className="underline" data-testid={`resume-job-${j.job_id}`}>
                        Resume
                      </button>
                    )}
                    {(active || paused) && (
                      <button type="button" onClick={() => cancelJob(j.job_id)} className="underline" data-testid={`cancel-job-${j.job_id}`}>
                        Cancel
                      </button>
                    )}
                    {!active && !paused && j.failed > 0 && !j.retried_at && (
                      <button type="button" onClick={() => jobAction(j.job_id, "retry-failed")} className="underline" data-testid={`retry-job-${j.job_id}`}>
                        Retry failed
                      </button>
                    )}
                  </span>
                </div>
                <Progress value={pct} className="h-1.5 my-1.5" />
                <div className="flex justify-between text-xs font-mono text-muted-foreground">
                  <span>
                    {fmt(j.deleted)} / {fmt(j.total)} deleted
                    {j.failed ? <span className="text-destructive"> · {fmt(j.failed)} failed</span> : null}
                  </span>
                  {j.rate_per_min ? <span>{fmt(j.rate_per_min)}/min</span> : null}
                </div>
                {j.error && <div className="text-xs text-destructive mt-1">{j.error}</div>}
              </div>
            );
          })}
        </div>
      </Panel>

      {target && (
        <div
          className="fixed bottom-9 inset-x-0 z-30 border-t border-border bg-background px-4 py-3 flex items-center justify-between gap-3"
          data-testid="selection-bar"
        >
          <div className="text-sm font-mono min-w-0 truncate">
            {target.label} ·{" "}
            <b data-testid="match-count">
              {target.count === null || target.count === undefined
                ? preview.loading ? "counting…" : "—"
                : `${fmt(target.count)} messages`}
            </b>
          </div>
          <div className="flex gap-2 shrink-0">
            {view === "summary" && (
              <Button variant="outline" size="sm" onClick={() => setDrillOpen(true)} data-testid="preview-messages">
                Review
              </Button>
            )}
            <Button
              variant="outline"
              size="sm"
              onClick={() => {
                setPicked(new Set());
                setAllMatching(false);
                clearSel();
              }}
              data-testid="clear-selection"
            >
              <X size={14} className="mr-1" /> Clear
            </Button>
            {archivedView ? (
              <Button variant="outline" size="sm" disabled={!target.count || submitting} onClick={() => runAction("restore")} data-testid="restore-selected">
                Restore
              </Button>
            ) : (
              <Button variant="outline" size="sm" disabled={!target.count || submitting} onClick={() => runAction("archive")} data-testid="archive-selected">
                Archive
              </Button>
            )}
            <Button
              size="sm"
              variant="destructive"
              disabled={!target.count || targetDeleting || submitting}
              title={targetDeleting ? "A deletion is already running for this filter" : undefined}
              onClick={() => setConfirmKind("delete")}
              data-testid="delete-selected"
            >
              <Trash size={14} className="mr-1" /> Delete
            </Button>
            {targetDeleting && <span className="text-xs text-muted-foreground" data-testid="deletion-running-notice">Deletion in progress</span>}
          </div>
        </div>
      )}

      <StatusBar
        jobs={jobs}
        archive={archiveJob}
        refreshKey={extrasKey}
        onRetry={(jobId) => jobAction(jobId, "retry-failed")}
      />

      <MessageDrilldown
        open={drillOpen}
        onOpenChange={setDrillOpen}
        accountId={activeId}
        filter={summaryFilter}
        onDeleted={() => {
          loadJobs(activeId);
          loadSummary(activeId, { silent: true, q: senderQ });
          setExtrasKey((k) => k + 1);
        }}
      />

      <AlertDialog open={confirmKind === "delete"} onOpenChange={(o) => !o && setConfirmKind(null)}>
        <AlertDialogContent data-testid="confirm-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete {fmt(target?.count)} messages?</AlertDialogTitle>
            <AlertDialogDescription>
              {target?.label === "all matching"
                ? "Everything matching the current filter"
                : view !== "summary" && !allMatching
                  ? `Only the ${target?.label || ""} messages`
                  : `Everything matching ${target?.label || ""}`}{" "}
              will be removed from this account. The job keeps running in the background if you leave this page.
              {view !== "summary" && !allMatching && preview.count > (target?.count || 0) && (
                <b className="block mt-2">
                  {fmt(preview.count)} messages match this filter. Cancel and click "Select all {fmt(preview.count)} matching this filter to delete them all.""
                </b>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                runAction("delete");
              }}
              disabled={submitting || targetDeleting}
              data-testid="confirm-delete-button"
            >
              {submitting ? "Starting…" : `Delete ${fmt(target?.count)}`}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
